// Single source of truth for carbon credit types
export const CREDIT_TYPES = {
  BIOCHAR: "Biochar",
  REFORESTATION: "Reforestation",
  AFFORESTATION: "Afforestation",
  REDD_PLUS: "REDD+",
  MANAGED_FORESTRY: "Managed Forestry (IFM)",
  BLUE_CARBON: "Blue Carbon",
  METHANE_CAPTURE: "Methane Capture",
  RENEWABLE_ENERGY: "Renewable Energy",
  SOIL_CARBON: "Soil Carbon",
  COOK_STOVES: "Improved Cook Stoves",
  ENHANCED_ROCK_WEATHERING: "Enhanced Rock Weathering",
  CONSTRUCTION_CARBON_SINK: "Construction Carbon Sink",
  GLOBAL_COOLING: "Global Cooling Services",
} as const

export type CreditType = typeof CREDIT_TYPES[keyof typeof CREDIT_TYPES]

export const CREDIT_TYPE_VALUES = Object.values(CREDIT_TYPES)

// Tonnes CO2e per hectare per year — used as default mint multiplier
export const METHODOLOGY_FACTORS: Record<CreditType, number> = {
  [CREDIT_TYPES.BIOCHAR]: 1.0,
  [CREDIT_TYPES.REFORESTATION]: 5.0,
  [CREDIT_TYPES.AFFORESTATION]: 4.5,
  [CREDIT_TYPES.REDD_PLUS]: 6.0,
  [CREDIT_TYPES.MANAGED_FORESTRY]: 3.5,
  [CREDIT_TYPES.BLUE_CARBON]: 8.0,
  [CREDIT_TYPES.METHANE_CAPTURE]: 2.5,
  [CREDIT_TYPES.RENEWABLE_ENERGY]: 1.8,
  [CREDIT_TYPES.SOIL_CARBON]: 3.0,
  [CREDIT_TYPES.COOK_STOVES]: 1.2,
  [CREDIT_TYPES.ENHANCED_ROCK_WEATHERING]: 2.0,
  [CREDIT_TYPES.CONSTRUCTION_CARBON_SINK]: 1.5,
  [CREDIT_TYPES.GLOBAL_COOLING]: 1.0,
}

// Methodology code → CreditType mapping
// Keys are registry methodology codes, not credit type names
export const METHODOLOGY_TO_CREDIT_TYPE: Record<string, CreditType> = {
  // Verra VCS
  "VM0007": CREDIT_TYPES.REDD_PLUS,
  "VM0009": CREDIT_TYPES.REDD_PLUS,
  "VM0015": CREDIT_TYPES.REDD_PLUS,
  "VM0010": CREDIT_TYPES.MANAGED_FORESTRY,
  "VM0033": CREDIT_TYPES.BLUE_CARBON,
  "VM0042": CREDIT_TYPES.SOIL_CARBON,
  "VM0047": CREDIT_TYPES.AFFORESTATION,
  // CDM legacy
  "AR-ACM0003": CREDIT_TYPES.AFFORESTATION,
  "ACM0001": CREDIT_TYPES.METHANE_CAPTURE,
  // Puro.earth
  "Puro.earth - Biochar Carbon Removal": CREDIT_TYPES.BIOCHAR,
  "Puro.earth - Biomass Burial": CREDIT_TYPES.BIOCHAR,
}

// Macro category taxonomy — NBS vs Engineered
export const CREDIT_TYPE_MACRO: Record<CreditType, "NBS" | "Engineered"> = {
  [CREDIT_TYPES.REDD_PLUS]: "NBS",
  [CREDIT_TYPES.REFORESTATION]: "NBS",
  [CREDIT_TYPES.AFFORESTATION]: "NBS",
  [CREDIT_TYPES.MANAGED_FORESTRY]: "NBS",
  [CREDIT_TYPES.BLUE_CARBON]: "NBS",
  [CREDIT_TYPES.SOIL_CARBON]: "NBS",
  [CREDIT_TYPES.BIOCHAR]: "Engineered",
  [CREDIT_TYPES.METHANE_CAPTURE]: "Engineered",
  [CREDIT_TYPES.RENEWABLE_ENERGY]: "Engineered",
  [CREDIT_TYPES.COOK_STOVES]: "Engineered",
  [CREDIT_TYPES.ENHANCED_ROCK_WEATHERING]: "Engineered",
  [CREDIT_TYPES.CONSTRUCTION_CARBON_SINK]: "Engineered",
  [CREDIT_TYPES.GLOBAL_COOLING]: "Engineered",
}

// Display labels for sub-categories in the market tree
// REFORESTATION and AFFORESTATION both render as "ARR" per Sean's taxonomy
export const CREDIT_TYPE_LABEL: Record<CreditType, string> = {
  [CREDIT_TYPES.REDD_PLUS]: "REDD+",
  [CREDIT_TYPES.REFORESTATION]: "ARR",
  [CREDIT_TYPES.AFFORESTATION]: "ARR",
  [CREDIT_TYPES.MANAGED_FORESTRY]: "IFM",
  [CREDIT_TYPES.BLUE_CARBON]: "Blue Carbon",
  [CREDIT_TYPES.SOIL_CARBON]: "Soil Carbon",
  [CREDIT_TYPES.BIOCHAR]: "Biochar",
  [CREDIT_TYPES.METHANE_CAPTURE]: "Methane Capture",
  [CREDIT_TYPES.RENEWABLE_ENERGY]: "Renewable Energy",
  [CREDIT_TYPES.COOK_STOVES]: "Improved Cook Stoves",
  [CREDIT_TYPES.ENHANCED_ROCK_WEATHERING]: "Enhanced Rock Weathering",
  [CREDIT_TYPES.CONSTRUCTION_CARBON_SINK]: "Construction Carbon Sink",
  [CREDIT_TYPES.GLOBAL_COOLING]: "Global Cooling Services",
}
